// src/components/league-creation/league-type/DynastyConfiguration.js
import React from 'react';
import { Trophy, Shuffle, TrendingUp } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { parseIntOrPrev } from '../../../services/formUtils';
import LotterySettings from './LotterySettings';

const DynastyConfiguration = ({ formData, onInputChange, error }) => {
  return (
    <div 
      className={`${dynastyTheme.components.card.base} border ${dynastyTheme.classes.border.primary}/30`}
      style={{ 
        borderRadius: dynastyTheme.tokens.radius.lg,
        padding: dynastyTheme.tokens.spacing.xl
      }}
    >
      <div className="flex items-center mb-6" style={{ gap: dynastyTheme.tokens.spacing.sm }}>
        <Trophy className={dynastyTheme.classes.text.primary} style={{ width: '1.5rem', height: '1.5rem' }} />
        <h3 className={`${dynastyTheme.classes.text.white} text-xl font-bold`}>
          Dynasty Configuration
        </h3>
      </div>

      {/* Draft Rounds */}
      <div style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
        <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
          Draft Rounds
        </label>
        <input
          type="number"
          value={formData.draft_rounds}
          onChange={(e) => onInputChange('draft_rounds', parseIntOrPrev(e.target.value, formData.draft_rounds))}
          className={`${dynastyTheme.components.input} w-full`}
          min="1"
          max="40"
        />
        <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
          Total number of rounds in the draft (1-40)
        </p>
        {error && error.includes('Draft rounds') && (
          <p className={`${dynastyTheme.classes.text.error} text-sm mt-1`}>
            {error}
          </p>
        )}
      </div>

      {/* ML Draft Rounds */}
      <div style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
        <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
          ML Draft Rounds
        </label>
        <input
          type="number"
          value={formData.ml_draft_rounds}
          onChange={(e) => onInputChange('ml_draft_rounds', parseIntOrPrev(e.target.value, formData.ml_draft_rounds))}
          className={`${dynastyTheme.components.input} w-full`}
          min="1"
          max="10"
        />
        <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
          Number of rounds in the minor league draft (1-10)
        </p>
        {error && error.includes('ML draft rounds') && (
          <p className={`${dynastyTheme.classes.text.error} text-sm mt-1`}>
            {error}
          </p>
        )}
      </div>

      {/* Number of Keepers */}
      <div style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
        <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
          Number of Keepers
        </label>
        <input
          type="number"
          value={formData.num_keepers}
          onChange={(e) => onInputChange('num_keepers', parseIntOrPrev(e.target.value, formData.num_keepers))}
          className={`${dynastyTheme.components.input} w-full`}
          min="0"
          max={formData.draft_rounds}
        />
        <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
          How many players each team keeps each year (0-{formData.draft_rounds})
        </p>
        {error && error.includes('keepers') && (
          <p className={`${dynastyTheme.classes.text.error} text-sm mt-1`}>
            {error}
          </p>
        )}
      </div>

      {/* Initial Draft Type */}
      <div style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
        <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white} flex items-center`} style={{ gap: dynastyTheme.tokens.spacing.sm }}>
          <Shuffle style={{ width: '1rem', height: '1rem' }} />
          Initial Draft Type
        </label>
        <select
          value={formData.dynasty_initial_draft_type}
          onChange={(e) => onInputChange('dynasty_initial_draft_type', e.target.value)}
          className={`${dynastyTheme.components.input} w-full`}
        >
          <option value="snake">Snake Draft</option>
          <option value="random_repeating">Random (Same Order Each Round)</option>
          <option value="auction" disabled>Auction Draft (Coming Soon)</option>
        </select>
        <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
          How the first draft will work when the league starts
        </p>
        {error && error.includes('initial draft') && (
          <p className={`${dynastyTheme.classes.text.error} text-sm mt-1`}>
            {error}
          </p>
        )}
      </div>

      {/* Subsequent Draft Type */}
      <div style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
        <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white} flex items-center`} style={{ gap: dynastyTheme.tokens.spacing.sm }}>
          <TrendingUp style={{ width: '1rem', height: '1rem' }} />
          Subsequent Draft Type
        </label>
        <select
          value={formData.dynasty_subsequent_draft_type}
          onChange={(e) => onInputChange('dynasty_subsequent_draft_type', e.target.value)}
          className={`${dynastyTheme.components.input} w-full`}
        >
          <option value="basic_standings">Standings-Based (Worst Team Picks First)</option>
          <option value="standings_lottery">Standings with Lottery</option>
          <option value="auction" disabled>Auction Draft (Coming Soon)</option>
        </select>
        <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
          How drafts work after the first year
        </p>
        {error && error.includes('subsequent draft') && (
          <p className={`${dynastyTheme.classes.text.error} text-sm mt-1`}>
            {error}
          </p>
        )}
      </div>

      {/* Conditional Lottery Settings */}
      {formData.dynasty_subsequent_draft_type === 'standings_lottery' && (
        <div style={{ marginTop: dynastyTheme.tokens.spacing.xl }}>
          <LotterySettings
            formData={formData}
            onInputChange={onInputChange}
            error={error}
          />
        </div>
      )}

      {/* Dynasty Info Box */}
      <div 
        className={`${dynastyTheme.classes.bg.primary}/10 border ${dynastyTheme.classes.border.primary}/30 mt-6`}
        style={{ 
          borderRadius: dynastyTheme.tokens.radius.md,
          padding: dynastyTheme.tokens.spacing.md
        }}
      >
        <h4 className={`${dynastyTheme.classes.text.white} font-semibold mb-2 text-sm`}>
          Dynasty League Features
        </h4>
        <div className={`${dynastyTheme.classes.text.neutralLight} text-xs space-y-1`}>
          <p>• Multi-year player contracts with salary retention</p>
          <p>• Keeper league format with contract extensions</p>
          <p>• Long-term team building strategy required</p>
          <p>• Full season commitment with real MLB roster changes</p>
        </div>
      </div>
    </div>
  );
};

export default DynastyConfiguration;